import type { Degree } from "@pdfking/math"
import type { Math } from "@pdfking/quantity"
import type { PageNumber } from "@pdfking/renderer-api"
import { RecordMerge } from "@pdfking/tfunc"

import type { GrowContentMeta, GrowEdge, GrowLayout } from "#public"
import { DIM_HEIGHT, DIM_WIDTH, DIM_ZERO, GrowContentKind } from "#public"

/** A piece with no place in the reading order: a spine, a flap, a die. */
export const NO_PAGES: GrowContentMeta = {
  label: undefined,
  page_front: undefined,
  page_rear: undefined,
  rotation: 0,
  flip: undefined,
}

/** A piece whose front is page `front` of the grow and whose rear is `rear`. */
export const Pages = (
  front: number,
  rear: number,
  opts: Partial<GrowContentMeta> = {}
): GrowContentMeta => ({
  ...NO_PAGES,
  page_front: front as PageNumber,
  page_rear: rear as PageNumber,
  ...opts,
})

/** A face of a box with no page of its own: what the name block calls it and
 * the quarter turn it reads at. */
export const Face = (
  label: string,
  rotation: GrowContentMeta["rotation"] = 0
): GrowContentMeta => ({
  ...NO_PAGES,
  label,
  rotation,
})

/** Layouts stacked along the opening, `cross` across it when they don't say. */
export const Along = (layouts: GrowLayout[], cross: Math | undefined = undefined): GrowLayout => ({
  enum: "along",
  layouts,
  cross,
})

/** Layouts stacked across the opening, top first, `cross` along it when they don't say. */
export const Across = (layouts: GrowLayout[], cross: Math | undefined = undefined): GrowLayout => ({
  enum: "across",
  layouts,
  cross,
})

/** Layouts along the opening folded together: the fill never opens them up. */
export const Pair = (layouts: GrowLayout[]): GrowLayout => ({
  enum: "pair",
  layouts,
  cross: undefined,
})

/** A piece of `kind`, `size` along its parent, `cross` the other way when pinned. */
export const Content = (
  kind: GrowContentKind,
  size: Math,
  meta: GrowContentMeta = NO_PAGES,
  cross: Math | undefined = undefined
): GrowLayout => ({ enum: "content", kind, size, cross, meta })

/** The page itself: one piece as wide and as high as the page, pages 1 and
 * 2. It grows nothing: the pricing sees no grow (`CompileGrow`). */
export const PAGE_ITSELF: GrowLayout = Content(
  GrowContentKind.PAGE,
  DIM_WIDTH,
  Pages(1, 2),
  DIM_HEIGHT
)

/** A flap piece `size` deep, no pages on it. */
export const FlapPiece = (size: Math): GrowLayout => Content(GrowContentKind.FLAP, size)

/** Parts stacked out of `edge`. */
export const Outward = (edge: GrowEdge, parts: GrowLayout[]): GrowLayout =>
  edge === "left" || edge === "right" ? Along(parts) : Across(parts)

/** Room nobody's: the pieces beside it bleed into it. */
export const Bleed = (size: Math): GrowLayout => ({ enum: "bleed", size })

/** The crease between the layouts either side: `angle` degrees folded, rear
 * onto rear when positive, opened at `step`, 0 never. */
export const Fold = (angle: number, step: number, ajar = 0): GrowLayout => ({
  enum: "fold",
  angle: angle as Degree,
  step,
  ajar,
})

/** What a flap says beyond its edge and its layout: the turn it makes
 * folded and the step it opens at, as a fold's; where it runs along the
 * edge, the whole edge when unsaid; how its tip tapers; how far ajar. */
export type FlapOptions = {
  angle: number
  step: number
  from: Math
  length: Math | undefined
  taper: [start: Math, end: Math]
  ajar: number
}

const FLAP_DEFAULTS: FlapOptions = {
  angle: 0,
  step: 0,
  from: DIM_ZERO,
  length: undefined,
  taper: [DIM_ZERO, DIM_ZERO],
  ajar: 0,
}

/** A flap off `edge` of the layout before it, `layout` stacked outward from
 * the edge, turning `angle` degrees folded at `step` as a fold does — a tab
 * never turns, the default —; along the edge `from` past its start, `length`
 * long, to the edge's end when unsaid, its tip `taper` narrower at either
 * end; `ajar` as a fold's. */
export const Flap = (
  edge: GrowEdge,
  layout: GrowLayout,
  options: Partial<FlapOptions> = {}
): GrowLayout => {
  const merged = RecordMerge(FLAP_DEFAULTS, options)
  const { angle, step, from, length, taper, ajar } = merged
  return { enum: "flap", edge, from, length, taper, angle: angle as Degree, step, ajar, layout }
}
